import { useCallback, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { selectCanUseGrow, selectClinicId, selectIsGrowIntegrated } from "../store/slices/authSlice";
import { useCancelGrowPaymentLinkMutation } from "../store/services/growApi";
import { getGrowLink, GROW_ACTIVE_LINK_STATUSES } from "../firebase";
import { useToast } from "../contexts/ToastContext";
import GrowCancelLinkPrompt from "../components/grow/GrowCancelLinkPrompt";

/**
 * useCancelGrowLinkOnManualPayment — manual-paid conflict guard (Phase 5.6).
 *
 * Wherever a transaction is marked paid / cancelled by hand, call
 * `await ensureNoActiveLink(transaction, reason)` BEFORE the existing status
 * update (`reason` = "paid" (default) | "cancel" — picks the prompt copy):
 *   - no `growLinkId` or link not active → resolves true immediately (no UI)
 *   - active link → the transaction is LOCKED (decision 2026-10-27): the prompt
 *     offers only "ביטול דרישת התשלום והמשך" (cancels at Grow, then true) or
 *     "חזרה" (false → caller aborts). There is no "continue without cancelling".
 *   - link cannot be read → false + toast (fail closed; never collect twice)
 * Render `{prompt}` once in the host component.
 * The existing status-update logic is never touched by this hook.
 *
 * Error code: CK-3872
 */
export default function useCancelGrowLinkOnManualPayment() {
  const clinicId = useSelector(selectClinicId);
  const integrated = useSelector(selectIsGrowIntegrated);
  const canUseGrow = useSelector(selectCanUseGrow);
  const [cancelLink, { isLoading }] = useCancelGrowPaymentLinkMutation();
  const { showError } = useToast();
  const [pending, setPending] = useState(null); // { link, transaction, reason }
  const resolverRef = useRef(null);

  const settle = useCallback((value) => {
    const r = resolverRef.current;
    resolverRef.current = null;
    setPending(null);
    if (r) r(value);
  }, []);

  const ensureNoActiveLink = useCallback(
    async (transaction, reason = "paid") => {
      const linkId = transaction?.growLinkId;
      if (!linkId || !integrated || !clinicId) return true;
      let link = null;
      try {
        link = await getGrowLink(clinicId, linkId);
      } catch (error) {
        console.error("[CK-3872] Could not load Grow link before manual payment", {
          errorCode: "CK-3872",
          errorMessage: error.message,
          linkId,
          timestamp: new Date().toISOString(),
        });
        // Fail CLOSED (decision 2026-10-27): a transaction stamped with a Grow link is locked
        // until we can prove the link is no longer active — never risk collecting twice.
        showError("לא ניתן לאמת את מצב דרישת התשלום ב-Grow. נסו שוב בעוד רגע.");
        return false;
      }
      if (!link || !GROW_ACTIVE_LINK_STATUSES.includes(link.status)) return true;
      return new Promise((resolve) => {
        resolverRef.current = resolve;
        setPending({ link, transaction, reason });
      });
    },
    [clinicId, integrated, showError]
  );

  const onCancelAndContinue = useCallback(async () => {
    if (!pending) return;
    const res = await cancelLink({ clinicId, linkId: pending.link.id });
    if (res.error) {
      console.error("[CK-3872] Cancel Grow link on manual payment failed", {
        errorCode: "CK-3872",
        errorMessage: res.error.message,
        linkId: pending.link.id,
        timestamp: new Date().toISOString(),
      });
      // The link may still be payable — tell the user and let them decide again.
      showError(`ביטול הקישור ב-Grow נכשל: ${res.error.message}`);
      return; // prompt stays open
    }
    settle(true);
  }, [pending, cancelLink, clinicId, settle, showError]);

  const prompt = pending ? (
    <GrowCancelLinkPrompt
      link={pending.link}
      reason={pending.reason}
      busy={isLoading}
      canCancel={canUseGrow}
      onCancelAndContinue={onCancelAndContinue}
      onBack={() => settle(false)}
    />
  ) : null;

  return { ensureNoActiveLink, prompt, isPrompting: !!pending };
}
